import {
  type RangePreset,
  TimeSuggestionFilters,
} from "@/components/analysis/time-suggestion-filters";
import {
  type TimeQueueTab,
  TimeSuggestionQueue,
} from "@/components/analysis/time-suggestion-queue";
import { TrackerPublishCard } from "@/components/analysis/tracker-publish-card";
import { api } from "@/lib/api";
import { paginationSearchFields } from "@/lib/search";
import { Link, createFileRoute, useLoaderData, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";

/**
 * `/suggestions/time-entries` — **your own** proposed time entries: what your
 * automatic time tracker pushed in over the API, and the attendance time the AI
 * proposed after a meeting you were in.
 *
 * Open to everyone, and deliberately scoped to the signed-in person even for
 * admins — confirming your own hours and reviewing the team's are two different
 * jobs, and mixing them buries the first in the second. The team-wide view is
 * `/suggestions/time-entries-all`.
 *
 * Filters live in the URL, like every other list in Hub: a week you're working
 * through survives a reload, and "here's what I'm looking at" is a link.
 *
 * Confirming creates the real time entry server-side; nothing is logged until
 * someone says so.
 *
 * E2E behaviour contract lives in `e2e/suggestions/time-suggestions.spec.ts`.
 */

const searchSchema = z.object({
  status: z.enum(["pending", "approved", "rejected", "all"]).optional(),
  projectId: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  ...paginationSearchFields,
});

/** The ranges someone confirming their own time actually reaches for. */
const PRESETS: RangePreset[] = ["today", "this-week", "last-week", "this-month"];

export const Route = createFileRoute("/_app/suggestions/time-entries")({
  validateSearch: searchSchema,
  component: MyTimeSuggestionsPage,
});

function MyTimeSuggestionsPage() {
  const { user } = useLoaderData({ from: "/_app" });
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    api
      .get<{ projects: Array<{ id: string; name: string }> }>("/api/projects?pageSize=200")
      .then((res) => setProjects(res.projects.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => {
        /* the project filter just stays empty */
      });
  }, []);

  const tab: TimeQueueTab = search.status ?? "pending";

  return (
    <div className="w-full space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Time suggestions</h1>
        <p className="text-muted-foreground mt-1">
          Proposed time entries from your time tracker and from meetings you attended. Nothing is
          logged until you confirm it, and today's is confirmed tomorrow — while you're working, its
          length can still grow. Connect a tracker, and check that meetings can recognise you, on
          your{" "}
          <Link to="/profile" className="underline">
            profile
          </Link>
          .
        </p>
      </div>

      <TrackerPublishCard />

      <TimeSuggestionFilters
        values={search}
        onChange={(next) =>
          // Narrowing the list makes the page you were on meaningless — back
          // to the first one, or a filter can land you on an empty page.
          navigate({
            to: "/suggestions/time-entries",
            search: {
              ...next,
              status: search.status,
              pageSize: search.pageSize,
              page: undefined,
            },
          })
        }
        projects={projects}
        presets={PRESETS}
      />

      <TimeSuggestionQueue
        tab={tab}
        onTabChange={(t) =>
          navigate({
            to: "/suggestions/time-entries",
            search: { ...search, status: t === "pending" ? undefined : t, page: undefined },
          })
        }
        projects={projects}
        scope="mine"
        canPair={user.globalRole === "admin"}
        filters={search}
        page={search.page}
        pageSize={search.pageSize}
        basePath="/suggestions/time-entries"
        searchParams={{
          status: search.status,
          projectId: search.projectId,
          from: search.from,
          to: search.to,
          pageSize: search.pageSize,
        }}
      />
    </div>
  );
}
